import React, { useEffect, useMemo, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const DAYS = [
  ['mon', 'Montag'], ['tue', 'Dienstag'], ['wed', 'Mittwoch'], ['thu', 'Donnerstag'],
  ['fri', 'Freitag'], ['sat', 'Samstag'], ['sun', 'Sonntag'],
];
const defaultSchedule = () => Object.fromEntries(DAYS.map(([day]) => [day, {
  open: !['sat', 'sun'].includes(day),
  intervals: !['sat', 'sun'].includes(day) ? [{ start_time: '08:00', end_time: '18:00' }] : [],
}]));
const EMPTY_SETTINGS = {
  enabled: false,
  time_zone: 'Europe/Berlin',
  schedule: defaultSchedule(),
  waiting_delay_minutes: 3,
  messages: { de: { within: '', outside: '', waiting: '' }, en: { within: '', outside: '', waiting: '' } },
  change_history: [],
};

function cloneSettings(value) {
  const cloned = JSON.parse(JSON.stringify(value || EMPTY_SETTINGS));
  if (!cloned.schedule) {
    cloned.schedule = Object.fromEntries(DAYS.map(([day]) => [day, {
      open: cloned.weekdays?.includes(day) || false,
      intervals: cloned.weekdays?.includes(day) ? [{ start_time: cloned.start_time, end_time: cloned.end_time }] : [],
    }]));
  }
  cloned.waiting_delay_minutes = Number(cloned.waiting_delay_minutes || 3);
  for (const locale of ['de', 'en']) cloned.messages[locale].waiting ||= locale === 'de'
    ? 'Aktuell ist leider kein Mitarbeiter verfügbar. Wir melden uns schnellstmöglich unter der von Ihnen angegebenen E-Mail-Adresse.'
    : 'Unfortunately, no support agent is currently available. We will contact you as soon as possible at the email address you provided.';
  return cloned;
}

export default function ChatAutomationSettingsCard({ settings, token, onSaved }) {
  const [draft, setDraft] = useState(() => cloneSettings(settings));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  useEffect(() => setDraft(cloneSettings(settings)), [settings]);
  const payload = useMemo(() => ({ enabled: Boolean(draft.enabled), waiting_delay_minutes: Number(draft.waiting_delay_minutes), schedule: draft.schedule, messages: draft.messages }), [draft]);

  const updateDay = (day, updater) => setDraft((current) => ({
    ...current,
    schedule: { ...current.schedule, [day]: updater(current.schedule[day]) },
  }));
  const toggleDay = (day, checked) => updateDay(day, (entry) => ({
    open: checked,
    intervals: checked ? (entry.intervals.length ? entry.intervals : [{ start_time: '08:00', end_time: day === 'fri' ? '15:00' : '18:00' }]) : [],
  }));
  const updateInterval = (day, index, field, value) => updateDay(day, (entry) => ({
    ...entry,
    intervals: entry.intervals.map((interval, current) => current === index ? { ...interval, [field]: value } : interval),
  }));
  const addInterval = (day) => updateDay(day, (entry) => ({ ...entry, intervals: [...entry.intervals, { start_time: '13:00', end_time: '18:00' }] }));
  const removeInterval = (day, index) => updateDay(day, (entry) => ({ ...entry, intervals: entry.intervals.filter((_, current) => current !== index) }));
  const updateMessage = (locale, windowName, value) => setDraft((current) => ({
    ...current,
    messages: { ...current.messages, [locale]: { ...current.messages[locale], [windowName]: value } },
  }));

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const response = await fetch(`${API_URL}/admin/settings/chat-auto-acknowledgement`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Chat-Einstellungen konnten nicht gespeichert werden.');
      setDraft(cloneSettings(data.chat_auto_acknowledgement));
      setSuccess(data.message || 'Einstellungen gespeichert.');
      onSaved?.(data.chat_auto_acknowledgement);
    } catch (saveError) { setError(saveError.message || 'Chat-Einstellungen konnten nicht gespeichert werden.'); }
    finally { setSaving(false); }
  };
  const rollback = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const response = await fetch(`${API_URL}/admin/settings/chat-auto-acknowledgement/rollback`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Vorherige Konfiguration konnte nicht wiederhergestellt werden.');
      setDraft(cloneSettings(data.chat_auto_acknowledgement));
      setSuccess(data.message);
      onSaved?.(data.chat_auto_acknowledgement);
    } catch (rollbackError) { setError(rollbackError.message); }
    finally { setSaving(false); }
  };

  return (
    <section id="chat-automation" className="card admin-section-card admin-section-anchor chat-automation" aria-labelledby="chat-automation-title">
      <div className="admin-section-header">
        <div>
          <h2 id="chat-automation-title">Live-Chat-Automatisierung</h2>
          <p className="subtitle">Genau eine Eingangsbestätigung bei der ersten Nachricht einer neuen Chatsitzung. Zeitzone: Europe/Berlin (MEZ/MESZ).</p>
        </div>
        <label className="chat-automation__activation">
          <input type="checkbox" checked={Boolean(draft.enabled)} onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))} />
          Automatische Bestätigung aktiv
        </label>
      </div>

      <fieldset className="chat-automation__fieldset">
        <legend>Geschäftszeiten je Wochentag</legend>
        <div className="chat-automation__schedule">
          {DAYS.map(([day, label]) => {
            const entry = draft.schedule[day];
            return (
              <div className="chat-automation__day" key={day}>
                <label className="chat-automation__day-toggle">
                  <input type="checkbox" checked={entry.open} onChange={(event) => toggleDay(day, event.target.checked)} />
                  <strong>{label}</strong><span>{entry.open ? 'geöffnet' : 'geschlossen'}</span>
                </label>
                {entry.open ? (
                  <div className="chat-automation__intervals">
                    {entry.intervals.map((interval, index) => (
                      <div className="chat-automation__interval" key={`${day}-${index}`}>
                        <label>{label} von<input aria-label={`${label} von ${index + 1}`} type="time" value={interval.start_time} onChange={(event) => updateInterval(day, index, 'start_time', event.target.value)} /></label>
                        <label>{label} bis<input aria-label={`${label} bis ${index + 1}`} type="time" value={interval.end_time} onChange={(event) => updateInterval(day, index, 'end_time', event.target.value)} /></label>
                        {entry.intervals.length > 1 ? <button type="button" className="secondary-button" onClick={() => removeInterval(day, index)}>Zeitraum entfernen</button> : null}
                      </div>
                    ))}
                    <button type="button" className="secondary-button" onClick={() => addInterval(day)}>Weiteren Zeitraum hinzufügen</button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="chat-automation__fieldset">
        <legend>Wartehinweis ohne Mitarbeiterantwort</legend>
        <label className="chat-automation__message">
          <span>Wartezeit in Minuten</span>
          <input type="number" min="1" max="30" value={draft.waiting_delay_minutes} onChange={(event) => setDraft((current) => ({ ...current, waiting_delay_minutes: Number(event.target.value) }))} />
          <small>Der Hinweis wird pro Unterhaltung höchstens einmal und nur dann angezeigt, wenn noch keine Mitarbeiterantwort vorliegt.</small>
        </label>
      </fieldset>

      <div className="chat-automation__message-grid">
        {['de', 'en'].map((locale) => (
          <fieldset className="chat-automation__fieldset" key={locale}>
            <legend>{locale === 'de' ? 'Deutsche automatische Nachrichten' : 'English automatic messages'}</legend>
            {['within', 'outside', 'waiting'].map((windowName) => {
              const text = draft.messages?.[locale]?.[windowName] || '';
              const label = windowName === 'within' ? 'Nachricht innerhalb der Geschäftszeiten' : windowName === 'outside' ? 'Nachricht außerhalb der Geschäftszeiten' : 'Nachricht nach der Wartezeit ohne Mitarbeiterantwort';
              return (
                <label className="chat-automation__message" key={windowName}>
                  <span>{label}</span>
                  <textarea maxLength={500} value={text} onChange={(event) => updateMessage(locale, windowName, event.target.value)} aria-describedby={`${locale}-${windowName}-count`} />
                  <small id={`${locale}-${windowName}-count`}>{text.length}/500 Zeichen</small>
                  <span className="chat-automation__preview"><strong>Vorschau:</strong> {text || 'Noch kein Text eingegeben.'}</span>
                </label>
              );
            })}
          </fieldset>
        ))}
      </div>
      {error ? <p className="error-box" role="alert">{error}</p> : null}
      {success ? <p className="success-box" role="status">{success}</p> : null}
      <div className="chat-automation__footer">
        <button type="button" className="primary-button" onClick={save} disabled={saving}>{saving ? 'Speichert…' : 'Einstellungen speichern'}</button>
        <button type="button" className="secondary-button" onClick={rollback} disabled={saving || !draft.rollback_available}>Vorherige Konfiguration wiederherstellen</button>
        <span className="meta-text">Letzte Änderung: {draft.updated_at ? new Date(draft.updated_at).toLocaleString('de-DE') : 'noch keine'}</span>
      </div>
      {draft.change_history?.length ? (
        <details className="chat-automation__history">
          <summary>Datensparsamer Änderungsnachweis ({draft.change_history.length})</summary>
          <ol>{draft.change_history.map((item) => <li key={`${item.at}-${item.field_groups.join('-')}`}>{new Date(item.at).toLocaleString('de-DE')} · {item.field_groups.join(', ')}</li>)}</ol>
        </details>
      ) : null}
    </section>
  );
}
